import * as Yup from 'yup'

// Standardized password validation pattern and messages
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/
const PASSWORD_ERROR_MESSAGE =
  'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character'

// Storage quota constant: 10 MB in GB (10 * 1024 * 1024 bytes / (1024^3) ≈ 0.00977 GB)
export const MIN_USER_STORAGE_QUOTA_GB = 10 / 1024

// ================================
// Utility Validation Functions for HeroControl
// ================================

/**
 * Validates if a value is required (not empty)
 */
export const validateRequired =
  (fieldName: string) =>
  (value: string): string | undefined => {
    if (!value || value.trim().length === 0) {
      return `${fieldName} is required`
    }

    return
  }

/**
 * Validates email format
 */
export const validateEmail = (value: string): string | undefined => {
  if (!value) {
    return 'Email is required'
  }

  // Regex adapted from validator.js isEmail()
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  if (!emailRegex.test(value)) {
    return 'Invalid email format'
  }

  return undefined
}

/**
 * Validates password strength
 */
export const validatePassword = (value: string): string | undefined => {
  if (!value) {
    return 'Password is required'
  }
  if (value.length < 8) {
    return 'Password must be at least 8 characters long'
  }
  if (!/[A-Z]/.test(value)) {
    return 'Password must contain at least one uppercase letter'
  }
  if (!/[a-z]/.test(value)) {
    return 'Password must contain at least one lowercase letter'
  }
  if (!/[0-9]/.test(value)) {
    return 'Password must contain at least one number'
  }
  if (!/[!@#$%^&*]/.test(value)) {
    return 'Password must contain at least one special character'
  }

  return undefined
}

/**
 * Validates password confirmation
 */
export const validatePasswordConfirmation =
  (originalPassword: string) =>
  (value: string): string | undefined => {
    if (!value) {
      return 'Re-type Password is required'
    }
    if (value !== originalPassword) {
      return 'Passwords do not match'
    }

    return undefined
  }

/**
 * Validates phone number format
 */
export const validatePhoneNumber = (value: string): string | undefined => {
  if (!value) {
    return 'Phone number is required'
  }
  // Regex for format: +1 555 555 5555 or +1 (555) 555-5555
  const phoneRegex =
    /^(\+1 \([2-9][0-9]{2}\) [2-9][0-9]{2}-[0-9]{4}|\+1 [2-9][0-9]{2} [2-9][0-9]{2} [0-9]{4})$/

  if (!phoneRegex.test(value)) {
    return 'Please match the requested format: +1 555 555 5555'
  }

  return undefined
}

/**
 * Validates text with minimum and maximum length
 */
export const validateTextLength =
  (fieldName: string, minLength: number = 2, maxLength: number = 100) =>
  (value: string): string | undefined => {
    if (!value || value.trim().length === 0) {
      return `${fieldName} is required`
    }
    if (value.trim().length < minLength) {
      return `${fieldName} must be at least ${minLength} characters`
    }
    if (value.trim().length > maxLength) {
      return `${fieldName} cannot exceed ${maxLength} characters`
    }

    return undefined
  }

/**
 * Validates name fields (first name, last name)
 */
export const validateName =
  (fieldName: string) =>
  (value: string): string | undefined => {
    if (!value || value.trim().length === 0) {
      return `${fieldName} is required`
    }
    if (value.trim().length < 2) {
      return `${fieldName} must be at least 2 characters`
    }
    if (value.trim().length > 50) {
      return `${fieldName} cannot exceed 50 characters`
    }
    if (!/^[a-zA-Z\s]+$/.test(value)) {
      return `${fieldName} can only contain letters and spaces`
    }

    return undefined
  }

/**
 * Validates company name
 */
export const validateCompanyName = (value: string): string | undefined => {
  if (!value || value.trim().length === 0) {
    return 'Company Name is required'
  }
  if (value.trim().length < 2) {
    return 'Company Name must be at least 2 characters'
  }
  if (value.trim().length > 255) {
    return 'Company Name cannot exceed 255 characters'
  }
  if (!/^[a-zA-Z0-9\s&.-]+$/.test(value)) {
    return 'Company Name can only contain letters, numbers, spaces, &, ., and -'
  }

  return undefined
}

/**
 * Validates numeric values with min and max
 */
export const validateNumber =
  (fieldName: string, min?: number, max?: number) =>
  (value: string): string | undefined => {
    if (!value || value.trim().length === 0) {
      return `${fieldName} is required`
    }

    const numValue = parseFloat(value)

    if (isNaN(numValue)) {
      return `${fieldName} must be a valid number`
    }

    if (min !== undefined && numValue < min) {
      return `${fieldName} must be at least ${min}`
    }

    if (max !== undefined && numValue > max) {
      return `${fieldName} cannot exceed ${max}`
    }

    return undefined
  }

export const validationSchemaCustomDateRange = Yup.object({
  startDate: Yup.string().required('Start date is required'),
  endDate: Yup.string().required('End date is required'),
}).test('date-range', 'End date must be after start date', function (value) {
  if (!value.startDate || !value.endDate) return true

  return new Date(value.endDate) >= new Date(value.startDate)
})

export const loginValidationSchema = Yup.object({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
})

export const CompleteAccountValidationSchema = Yup.object({
  email: Yup.string().email('Invalid email format').required('Required'),
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(PASSWORD_REGEX, PASSWORD_ERROR_MESSAGE),
  retypePassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
})

export const updatePasswordValidationSchema = Yup.object({
  oldPassword: Yup.string()
    .required('Please enter your current password')
    .min(1, 'Please enter your current password'),
  newPassword: Yup.string()
    .required('Please enter a new password')
    .min(8, 'Password must be at least 8 characters long')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (!@#$%^&*)',
    )
    .test(
      'different-from-old',
      'New password must be different from current password',
      function (value) {
        const { oldPassword } = this.parent

        return value !== oldPassword
      },
    ),
  confirmPassword: Yup.string()
    .required('Please confirm your new password')
    .oneOf(
      [Yup.ref('newPassword')],
      'Passwords do not match. Please make sure both passwords are identical',
    ),
})

export const registerValidationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name cannot exceed 100 characters')
    .matches(/^[a-zA-Z0-9\s&.-]+$/, 'Name can only contain letters, numbers, spaces, &, ., and -'),

  firstName: Yup.string()
    .trim()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name cannot exceed 50 characters')
    .matches(/^[a-zA-Z\s]+$/, 'First name can only contain letters and spaces'),

  lastName: Yup.string()
    .trim()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name cannot exceed 50 characters')
    .matches(/^[a-zA-Z\s]+$/, 'Last name can only contain letters and spaces'),

  phoneNumber: Yup.string()
    .trim()
    .required('Phone number is required')
    .matches(
      /^(\+1 \([2-9][0-9]{2}\) [2-9][0-9]{2}-[0-9]{4}|\+1 [2-9][0-9]{2} [2-9][0-9]{2} [0-9]{4})$/,
      'Please match the requested format: +1 555 555 5555',
    ),

  adminEmail: Yup.string()
    .trim()
    .required('Email is required')
    .email('Please enter a valid email address'),

  adminPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(PASSWORD_REGEX, PASSWORD_ERROR_MESSAGE),

  retypePassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('adminPassword')], 'Passwords must match'),
})

export const addResellerValidationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required('Company name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name cannot exceed 100 characters')
    .matches(/^[a-zA-Z0-9\s&.-]+$/, 'Name can only contain letters, numbers, spaces, &, ., and -'),

  description: Yup.string()
    .trim()
    .required('Description is required')
    .min(2, 'Description must be at least 2 characters')
    .max(50, 'Description cannot exceed 50 characters')
    .matches(/^[a-zA-Z0-9\s-]+$/, 'Description can only contain letters, numbers, spaces, and -'),

  contactEmail: Yup.string()
    .trim()
    .required('Contact email is required')
    .min(2, 'Contact email must be at least 2 characters')
    .max(50, 'Contact email cannot exceed 50 characters')
    .email('Please enter a valid email address'),

  contactPerson: Yup.string()
    .trim()
    .required('Contact person is required')
    .min(2, 'Contact person must be at least 2 characters')
    .max(50, 'Contact person cannot exceed 50 characters')
    .matches(/^[a-zA-Z\s]+$/, 'Contact person can only contain letters and spaces'),

  phoneNumber: Yup.string().trim().required('Phone number is required'),
  addressLine1: Yup.string()
    .trim()
    .required('Address line 1 is required')
    .min(5, 'Address line 1 must be at least 5 characters')
    .max(200, 'Address line 1 cannot exceed 200 characters'),

  addressLine2: Yup.string()
    .trim()
    .optional()
    .min(5, 'Address line 2 must be at least 5 characters')
    .max(200, 'Address line 2 cannot exceed 200 characters'),

  zipcode: Yup.string()
    .trim()
    .required('Zip code is required')
    .min(3, 'Zip code must be at least 3 characters')
    .max(10, 'Zip code cannot exceed 10 characters'),

  city: Yup.string()
    .trim()
    .required('City is required')
    .min(2, 'City must be at least 2 characters')
    .max(50, 'City cannot exceed 50 characters'),

  state: Yup.string()
    .trim()
    .required('State is required')
    .min(2, 'State must be at least 2 characters')
    .max(50, 'State cannot exceed 50 characters'),

  countryCode: Yup.string()
    .trim()
    .required('Country code is required')
    .min(2, 'Country code must be at least 2 characters')
    .max(3, 'Country code cannot exceed 3 characters')
    .matches(/^[A-Z]{2,3}$/, 'Country code must be 2-3 uppercase letters'),

  storagePricePerGbCents: Yup.number()
    .required('Storage price per GB is required')
    .positive('Storage price per GB must be a positive number')
    .min(0.01, 'Storage price per GB must be at least 0.01')
    .max(1000, 'Storage price per GB cannot exceed 1000'),

  apiCallPriceCents: Yup.number()
    .required('API call price is required')
    .positive('API call price must be a positive number')
    .min(0.001, 'API call price must be at least 0.001')
    .max(100, 'API call price cannot exceed 100'),

  maxTenants: Yup.number()
    .nullable()
    .optional()
    .transform((val, orig) => (orig === '' || orig === undefined ? null : val))
    .integer('Maximum tenants must be a whole number')
    .min(1, 'Maximum tenants must be at least 1')
    .max(10000, 'Maximum tenants cannot exceed 10,000'),
})
export const validationSchemaAddUser = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phoneNumber: Yup.string().trim().required('Phone number is required'),
  storageQuota: Yup.number()
    .required('Storage quota is required')
    .min(MIN_USER_STORAGE_QUOTA_GB, `Storage quota must be at least 10 MB`),
  apiQuota: Yup.number().required('API quota is required').min(1, 'API quota must be at least 1'),
})

// Function to create validation schema with dynamic quota validation
export const createValidationSchemaAddUser = (currentUserQuotas?: {
  storageQuotaGB?: number
  apiQuota?: number
}) => {
  return Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    phoneNumber: Yup.string().trim().required('Phone number is required'),
    storageQuota: Yup.number()
      .required('Storage quota is required')
      .min(MIN_USER_STORAGE_QUOTA_GB, `Storage quota must be at least 10 MB`)
      .test('quota-limit', function (value) {
        if (!currentUserQuotas?.storageQuotaGB || !value) return true

        const { storageQuotaUnit } = this.parent
        let valueInGB = value

        // Convert to GB based on unit
        switch (storageQuotaUnit) {
          case 'TB':
            valueInGB = value * 1024
            break
          case 'PB':
            valueInGB = value * 1024 * 1024
            break
          case 'MB':
            valueInGB = value / 1024
            break
          case 'KB':
            valueInGB = value / (1024 * 1024)
            break
          case 'B':
            valueInGB = value / (1024 * 1024 * 1024)
            break
          // GB is default, no conversion needed
        }

        if (valueInGB > currentUserQuotas.storageQuotaGB) {
          return this.createError({
            message: `Storage quota cannot exceed your available quota (${currentUserQuotas.storageQuotaGB.toFixed(2)} GB)`,
          })
        }

        return true
      }),
    apiQuota: Yup.number()
      .required('API quota is required')
      .min(1, 'API quota must be at least 1')
      .test('api-quota-limit', function (value) {
        if (!currentUserQuotas?.apiQuota || !value) return true

        if (value > currentUserQuotas.apiQuota) {
          return this.createError({
            message: `API quota cannot exceed your available quota (${currentUserQuotas.apiQuota.toLocaleString()})`,
          })
        }

        return true
      }),
    storageQuotaUnit: Yup.string().required('Storage unit is required'),
  })
}

/**
 * Validates 6-digit verification code
 */
export const validateVerificationCode = (value: string): string | undefined => {
  if (!value) {
    return 'Verification code is required'
  }
  if (!/^\d{6}$/.test(value)) {
    return 'Please enter a valid 6-digit code'
  }
  if (value.length !== 6) {
    return 'Code must be exactly 6 digits'
  }

  return undefined
}
